import React, { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller } from 'react-hook-form';
import MenuItem from '@material-ui/core/MenuItem';
import PhysicianUtils from 'services/PhysicianUtils';
import StaffUtils from 'services/StaffUtils';
import { TopBar } from 'components/Patient/CPP/CPPInput/Inputs/CPPInputCommonViews';
import moment from 'moment-timezone';
import './style.scss';
import ValidatorUtils from 'services/ValidatorUtils';
import CalendarUtils from 'services/CalendarUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import Icon from 'components/Utils/NewIcon';
import PerformanceUtils from 'services/PerformanceUtils';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import { useReduxForm } from 'hooks/useReduxForm';

const DATE_FORMAT = 'YYYY/MM/DD';

const immunizationSchema = z.object({
  name: z.string().optional().or(z.literal('')),
  description: z.string().optional().or(z.literal('')),
  prescription: z.string().optional().or(z.literal('')),
  route: z.string().optional().or(z.literal('')),
  site: z.string().optional().or(z.literal('')),
  lot: z.string().optional().or(z.literal('')),
  expiry_date: z.string().optional().or(z.literal('')),
  given_date: z.string().optional().or(z.literal('')),
  scheduled_date: z.string().optional().or(z.literal('')),
  dosage: z.string().optional().or(z.literal('')),
  given_by: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
  transcribed: z.boolean().optional(),
  followUp: z.boolean().optional(),
  followUpDate: z.any(),
  catalog_item_id: z.string().optional().or(z.literal('')),
  feeId: z.string().optional().or(z.literal('')),
  paid: z.boolean().optional(),
  itemId: z.any(),
  patient_id: z.any(),
});

type ImmunizationValues = z.infer<typeof immunizationSchema>;

const NewImmunization = ({
  patient,
  onClose,
  form,
}: {
  patient: any;
  onClose?: () => void;
  form: string;
}) => {
  const [loading, setLoading] = useState(false);
  const [physicianNames, setPhysicianNames] = useState<string[]>([]);
  const [removing, setRemoving] = useState(false);
  // Guards double-click on "Save & Charge" — two in-flight submits would
  // charge the patient twice (same guard the administered-drug form has).
  const inFlightRef = useRef(false);

  const dispatch = useAppDispatch();
  const physicians = useAppSelector((state) => state.physicians.byId);
  const immunizationDescriptions = useAppSelector((state) => state.immunizationDescriptions);
  const authentication = useAppSelector((state) => state.authentication);

  const { control, handleSubmit, setValue, watch, reset, getValues } =
    useReduxForm<ImmunizationValues>(form, {
      destroyOnUnmount: false,
      resolver: zodResolver(immunizationSchema),
      defaultValues: {
        name: '',
        description: '',
        prescription: '',
        route: '',
        site: '',
        lot: '',
        expiry_date: '',
        given_date: '',
        scheduled_date: '',
        dosage: '',
        given_by: '',
        notes: '',
        transcribed: false,
        followUp: false,
        followUpDate: undefined,
        catalog_item_id: '',
        feeId: '',
        paid: false,
        itemId: undefined,
        patient_id: undefined,
      },
    });

  const itemId = watch('itemId');
  const given_date = watch('given_date');
  const scheduled_date = watch('scheduled_date');
  const expiry_date = watch('expiry_date');
  const name = watch('name');
  const feeId = watch('feeId');
  const catalogItemId = watch('catalog_item_id');
  const paid = watch('paid');
  const followUp = watch('followUp');

  useEffect(() => {
    getPhysicianNames();
    // Prices are tenant fee-schedule data now — refetch on every form open so
    // an admin's midday price change can't ride a stale login-time preload.
    dispatch(api.HealthRecords.getImmunizationDescriptions());
  }, []);

  useEffect(() => {
    if (itemId) onChangeImmunizationName(name);
  }, [itemId, name]);

  // Auto-convert unix timestamps to date strings
  useEffect(() => {
    const dates: Record<string, string | undefined> = { expiry_date, given_date, scheduled_date };
    Object.entries(dates).forEach(([key, val]) => {
      if (
        val &&
        ValidatorUtils.isInt(val) &&
        parseInt(val) > 9999 &&
        !ValidatorUtils.stringMatchesDate(val, DATE_FORMAT)
      ) {
        setValue(key as keyof ImmunizationValues, CalendarUtils.format(parseInt(val), DATE_FORMAT));
      }
    });
  }, [expiry_date, given_date, scheduled_date, setValue]);

  const getPhysicianNames = () => {
    let filteredPhysicians = StaffUtils.filterActiveUsers(physicians);

    let ownName: string | null = null;
    let names: string[] = filteredPhysicians.map((physician: any) => {
      let physicianName: string = PhysicianUtils.getName(physician) || '';
      if (physician.id === authentication.user!.id) ownName = physicianName;
      return physicianName;
    });

    const currentGivenBy = getValues('given_by');
    if (ownName && !currentGivenBy) {
      setValue('given_by', ownName);
    }
    setPhysicianNames(names);
  };

  const clearForm = () => {
    reset();
  };

  const isNew = !itemId;
  // Deactivated catalog items stay resolvable for historical records but are
  // excluded from new-record selection.
  let descriptions = immunizationDescriptions.filter((imm: any) => imm.active !== false);

  const buttonDisabled = !name || (!given_date && !scheduled_date) || removing;

  // The selected immunization's fee options (tenant fee schedule). Resolved by
  // catalog item id, falling back to name for records predating the catalog.
  const selectedDescription =
    descriptions.find((imm: any) => (catalogItemId ? imm.id === catalogItemId : false)) ||
    descriptions.find((imm: any) => imm.name === name);
  const fees: any[] = selectedDescription?.fees ?? [];
  const selectedFee = fees.find((fee: any) => fee.id === feeId);

  function onChangeImmunizationName(immunizationName: string | undefined) {
    if (!immunizationName) return;
    let immunization = immunizationDescriptions.filter((imm: any) => {
      return imm.name === immunizationName;
    })[0];

    if (immunization) {
      setValue('description', immunization.description);
      setValue('prescription', immunization.prescription);
      setValue('catalog_item_id', immunization.id || '');
      setValue('feeId', '');
    }
  }

  async function addImmunization(values: ImmunizationValues) {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      await submitImmunization(values);
    } finally {
      inFlightRef.current = false;
    }
  }

  async function submitImmunization(values: ImmunizationValues) {
    let value: any = PerformanceUtils.clone(values);
    let id = value.itemId;
    delete value.itemId;

    if (value.expiry_date) {
      value.expiry_date = CalendarUtils.convertToTargetTimezone(
        moment(value.expiry_date, DATE_FORMAT),
      ).unix();
    } else {
      delete value.expiry_date;
    }

    if (value.given_date) {
      value.given_date = CalendarUtils.convertToTargetTimezone(
        moment(value.given_date, DATE_FORMAT),
      ).unix();
    } else {
      delete value.given_date;
    }

    if (value.scheduled_date) {
      value.scheduled_date = CalendarUtils.convertToTargetTimezone(
        moment(value.scheduled_date, DATE_FORMAT),
      ).unix();
      if (!value.given_date) delete value.given_by;
    } else {
      delete value.scheduled_date;
    }

    let { followUp, followUpDate, feeId: chosenFeeId, paid } = value;
    delete value.followUp;
    delete value.followUpDate;
    delete value.feeId;
    delete value.paid;
    if (!value.catalog_item_id) delete value.catalog_item_id;

    let followUpImmunization =
      !!followUp && !!followUpDate
        ? {
            patient_id: patient.id,
            value: {
              ...value,
              given_date: undefined,
              expiry_date: undefined,
              lot: undefined,
              given_by: undefined,
              notes: undefined,
              scheduled_date: followUpDate,
            },
          }
        : undefined;

    let immunization = {
      id,
      patient_id: patient.id,
      value: {
        ...value,
        paid,
      },
    };

    console.log(immunization);

    setLoading(true);

    // The server resolves the amount from the fee reference — the client never
    // sends a raw amount.
    let chargeParams =
      chosenFeeId && !paid ? { feeEntryId: value.catalog_item_id, feeId: chosenFeeId } : {};

    let result = await dispatch(
      api.HealthRecords.addImmunization({ immunization, ...chargeParams }),
      {
        title: 'Immunization',
        successText: 'Immunization added successfully!',
      },
    );
    setLoading(false);
    if (result) {
      if (followUpImmunization)
        await dispatch(api.HealthRecords.addImmunization({ immunization: followUpImmunization }), {
          errorTitle: 'Follow-up Immunization',
        });
      reset();
    }
  }

  async function removeImmunization() {
    setRemoving(true);
    let result = await dispatch(
      api.HealthRecords.removeImmunization({
        id: itemId,
        patientId: patient.id,
      }),
      {
        title: 'Remove Immunization',
        successText: 'Immunization removed successfully!',
      },
    );
    setRemoving(false);
    if (result) {
      reset();
    }
  }

  return (
    <>
      {onClose !== undefined && (
        <TopBar title="Immunization" onClose={onClose} onClear={clearForm} />
      )}
      <div className="details-panel new-immunization">
        <div className="row form">
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <DefaultSelectField
                value={field.value ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(event.target.value);
                  onChangeImmunizationName(event.target.value);
                }}
                inputElements={descriptions.map((immunization: any, index: number) => {
                  let { name: immName } = immunization;
                  return (
                    <MenuItem value={immName} key={immName + index}>
                      {immName}
                    </MenuItem>
                  );
                })}
                selectedMenuItemStyle={{ color: '#2693d1' }}
                style={{ marginRight: '20px', width: '200px' }}
              />
            )}
          />
          <DefaultTextField
            name="description"
            control={control}
            label="Description"
            disabled={loading}
            style={{ width: '250px', marginRight: '20px' }}
          />
          <DefaultTextField
            name="prescription"
            control={control}
            label="Prescription"
            disabled={loading}
            style={{ width: '150px', marginRight: '20px' }}
          />
          <DefaultAutoComplete
            name="route"
            control={control}
            placeholder="Route"
            dataSource={autoCompleteSource.route}
            allowClear={true}
          />
          <DefaultAutoComplete
            name="site"
            control={control}
            placeholder="Site"
            dataSource={autoCompleteSource.site}
            allowClear={true}
          />
          <DefaultTextField
            name="lot"
            control={control}
            label="Lot #"
            disabled={loading}
            style={{ width: '150px', marginRight: '20px' }}
          />
          <DefaultTextField
            name="expiry_date"
            control={control}
            label={'Expiry ' + DATE_FORMAT}
            style={{ width: '170px', marginRight: '10px' }}
          />
          <DefaultTextField
            name="given_date"
            control={control}
            label={'Given ' + DATE_FORMAT}
            style={{ width: '170px', marginRight: '10px' }}
          />
          <div className="given-button-wrapper">
            <DefaultButton
              name="Given Today"
              onClick={() => {
                setValue('given_date', CalendarUtils.format(moment().unix(), 'YYYY/MM/DD'));
              }}
              style={{ width: '130px' }}
            />
          </div>
          <DefaultTextField
            name="dosage"
            control={control}
            label="Dosage (ml/vials)"
            disabled={loading}
            style={{ width: '150px', marginRight: '20px' }}
          />
          <DefaultSelectField
            name="given_by"
            control={control}
            label="Given By"
            inputs={['External', ...physicianNames]}
            style={{ width: '200px', marginRight: '20px', display: 'inline-block' }}
          />
          <DefaultTextField
            name="notes"
            control={control}
            label="Notes"
            disabled={loading}
            style={{ width: '320px', marginRight: '20px' }}
          />
          {!isNew && (
            <DefaultTextField
              name="scheduled_date"
              control={control}
              label={'Scheduled ' + DATE_FORMAT}
              style={{ width: '190px', marginRight: '10px' }}
            />
          )}
          <RHFCheckbox name="transcribed" control={control} />
        </div>
        <div className="follow-up">
          {paid && <span className="mr-3">This immunization has been successfully paid</span>}
          {fees.length === 1 && !paid && (
            <>
              <Controller
                name="feeId"
                control={control}
                render={({ field }) => (
                  <div
                    style={{
                      display: 'inline-block',
                      color: 'rgb(131, 131, 149)',
                      marginTop: '30px',
                    }}
                  >
                    <input
                      type="checkbox"
                      id="feeId"
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked ? fees[0].id : '')}
                    />
                    <span>{` Charge ${fees[0].label} — $${fees[0].amount}`}</span>
                  </div>
                )}
              />
              <Icon
                style={{ display: 'inline-block', margin: '0 15px 0 5px' }}
                src="circle-info"
                alt="If the immunization is being administered at the clinic, there is a cost associated with it and it should be charged"
                showTooltip={true}
                tooltipPlace="top"
                width="15px"
              />
            </>
          )}
          {fees.length > 1 && !paid && (
            <Controller
              name="feeId"
              control={control}
              render={({ field }) => (
                <DefaultSelectField
                  value={field.value ?? ''}
                  label="Charge"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    field.onChange(event.target.value)
                  }
                  inputElements={[
                    <MenuItem value="" key="no-charge">
                      No charge
                    </MenuItem>,
                    ...fees.map((fee: any) => (
                      <MenuItem value={fee.id} key={fee.id}>
                        {`${fee.label} — $${fee.amount}`}
                      </MenuItem>
                    )),
                  ]}
                  style={{ marginRight: '20px', width: '200px' }}
                />
              )}
            />
          )}
          <RHFCheckbox name="followUp" control={control} label="Schedule next dose?" />
          {!!followUp && (
            <DefaultDatePicker
              name="followUpDate"
              control={control}
              label="Next Dose Date"
              style={{ width: '145px', marginLeft: '15px', display: 'inline-block' }}
            />
          )}
        </div>
        <div className="button-wrapper">
          <DefaultButton
            name="Clear"
            onClick={clearForm}
            style={{ marginRight: '20px', width: '150px' }}
          />
          <DefaultButton
            name={selectedFee && !paid ? `Save & Charge $${selectedFee.amount}` : 'Save'}
            onClick={handleSubmit(addImmunization)}
            loading={loading}
            className="blue"
            style={selectedFee && !paid ? { width: '200px' } : undefined}
            disabled={buttonDisabled}
          />
          {!isNew && (
            <DefaultButton
              name="Remove"
              onClick={removeImmunization}
              className="red"
              disabled={loading}
              loading={removing}
              style={{ marginLeft: '20px', width: '150px' }}
            />
          )}
        </div>
      </div>
    </>
  );
};

const RHFCheckbox = ({
  name,
  control,
  id,
  label = 'Transcribed',
  style = {},
}: {
  name: string;
  control: any;
  id?: string;
  label?: string;
  style?: React.CSSProperties;
}) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => (
      <div
        style={{
          display: 'inline-block',
          borderBottom: fieldState.isTouched && fieldState.error ? '2px solid red' : 'none',
          color: 'rgb(131, 131, 149)',
          marginTop: '30px',
          ...style,
        }}
      >
        <input
          type="checkbox"
          id={id || name}
          name={name}
          checked={field.value === true}
          onChange={(e) => field.onChange(e.target.checked)}
          onBlur={field.onBlur}
          ref={field.ref}
        />
        <span> {label}</span>
      </div>
    )}
  />
);

export default NewImmunization;

const autoCompleteSource: Record<string, string[]> = {
  route: ['Intramuscular (IM)', 'Intradermal (ID)', 'Intranasal', 'Oral (PO)', 'Subcutaneous (SC)'],
  site: [
    'Left deltoid',
    'Right deltoid',
    'Left arm',
    'Right arm',
    'Left thigh',
    'Right thigh',
    'Left forearm',
    'Right forearm',
  ],
};
